import { useSyncExternalStore } from 'react';

/*
 * The operator app's test-workspace filter (IMM-593.2).
 *
 * One setting shared by every workspace-scoped list, remembered per operator
 * per browser: a view preference, not an account setting, so it lives in
 * `localStorage` — the same way the locale provider persists — and never on
 * the server. Off (hiding test data) is the state an operator arrives in, on a
 * fresh browser, after clearing storage, and whenever the stored value is
 * unreadable.
 *
 * React-router loaders run outside React, so the value is exposed both as a
 * plain read (`readIncludeTestWorkspaces`, for loaders) and as a hook
 * (`useIncludeTestWorkspaces`, for the pill and the rows). Both read the same
 * tiny external store, so a toggle on one list is already active on the next.
 */

export const INCLUDE_TEST_WORKSPACES_STORAGE_KEY = 'immo.admin.include-test-workspaces';

const ACTIVE = '1';
const INACTIVE = '0';

const listeners = new Set<() => void>();

function readStorage(): boolean {
  try {
    return window.localStorage.getItem(INCLUDE_TEST_WORKSPACES_STORAGE_KEY) === ACTIVE;
  } catch {
    // Private mode / storage disabled: hiding test data is the safe default.
    return false;
  }
}

function writeStorage(next: boolean): void {
  try {
    window.localStorage.setItem(INCLUDE_TEST_WORKSPACES_STORAGE_KEY, next ? ACTIVE : INACTIVE);
  } catch {
    // Quota / private mode: the value still flips in-memory for this tab via
    // the listeners; only the persistence-across-refresh promise is broken.
  }
}

// The in-memory mirror lets the hook stay consistent within a tab even when
// storage refuses the write, and is what `subscribe` notifies on.
let current = readStorage();

/** The current filter state — for loaders and other non-React readers. */
export function readIncludeTestWorkspaces(): boolean {
  return current;
}

/** Flips the shared filter, persists it, and notifies every mounted subscriber. */
export function setIncludeTestWorkspaces(next: boolean): void {
  if (next === current) return;
  current = next;
  writeStorage(next);

  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/** The shared filter state as React state — re-renders on every toggle, from any list. */
export function useIncludeTestWorkspaces(): boolean {
  return useSyncExternalStore(subscribe, readIncludeTestWorkspaces, () => false);
}

/**
 * The query fragment every workspace-scoped list endpoint takes. Omitted when
 * inactive — the server default is hiding, so the two can never disagree —
 * and the string `'true'` when active, as a URL parameter is sent.
 */
export function includeTestWorkspacesQuery(): { readonly includeTestWorkspaces?: 'true' } {
  return readIncludeTestWorkspaces() ? { includeTestWorkspaces: 'true' } : {};
}

/**
 * For supplementary reads on detail and write surfaces — a workspace's own
 * organizations, a key's request trail, a workspace picker — which are never gated
 * by the filter: the list endpoints are asked for everything, explicitly.
 */
export const ALL_WORKSPACES_QUERY = { includeTestWorkspaces: 'true' } as const;

/** Test seam: resets the in-memory mirror from storage (after a test wrote or cleared it). */
export function resyncIncludeTestWorkspacesFromStorage(): void {
  current = readStorage();

  for (const listener of listeners) listener();
}
